import {env} from '@baidu/boxx-fe';
import {useWebStorage} from '@lib/utils/native';
import {
    generateBrowseSchema,
    invokeSchema,
} from '@lib/utils/schema';
import {
    ACTIVITY_DATE,
    DEVICE_SIZE,
    NEW_USER_TIMES_KEY,
    REGEXP_EMAIL,
    REGEXP_EMOJI,
    REGEXP_REFERENCE_SUBSCRIPT,
} from '../constants';
import {
    sendYaLog,
    ubcLog,
} from '../log';
import {isPcClient} from './env';

export interface BuildParamsInterface {
    [propName: string]: string | boolean | number;
}

export const {isIOS, isAndroid, isHarmonyOS, boxVersion, versionCompare, OSVersion, UA} = env;
export const inNewApp = UA.includes('newapp');
export const isIpad = UA.includes('ipad');
export const isInMiniProgram = UA.includes('miniprogram'); // 注意boxx的env会转小写
export const isJSON = (value: string) => {
    const reJSON = /^\{[\s\S]*\}$|^\[[\s\S]*\]$/;
    if (typeof value !== 'string') {
        return false;
    }
    if (!reJSON.test(value)) {
        return false;
    }
    try {
        JSON.parse(value);
    }
    catch (err) {
        return false;
    }
    return true;
};

export function delay(ms = 100) {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
}

export const copyToClipBoard = (text: string) => {
    const tag = document.createElement('textarea');
    tag.setAttribute('readonly', '');
    tag.value = text;
    document.body.appendChild(tag);

    tag.select();
    tag.setSelectionRange(0, tag.value.length);

    tag.removeAttribute('readonly');

    try {
        document.execCommand('copy');
    }
    catch (error) { }

    try {
        tag.blur();
    }
    catch (error) { }

    tag.remove();
};

export function uuid() {
    return '_' + Math.random().toString(36).substring(2);
};

export const dealJump = (e: Event) => {
    // 屏蔽a标签的跳转
    // @ts-ignore
    if (e?.target?.nodeName === 'A') {
        e.stopImmediatePropagation();
        e.preventDefault();
        // @ts-ignore
        const url: string = e.target.href;
        // @ts-ignore
        const title: string = e.target.innerText || '';
        if (REGEXP_EMAIL.test(title)) {
            return;
        }
        ubcLog({
            params: {
                type: 'click',
                value: 'light_frame_skip',
                page: 'light_frame',
            },
            ext: {
                url,
                title,
                'session_id': '',
                'bot_id': '',
            },
        });
        invokeSchema(generateBrowseSchema({
            url,
            timeout: '4',
        }));
    }
};

export const dateFormat = (dateObj: Date, fmt: string) => {
    const o: Record<string, number> = {
        'M+': dateObj.getMonth() + 1,
        'd+': dateObj.getDate(),
        'h+': dateObj.getHours(),
        'm+': dateObj.getMinutes(),
        's+': dateObj.getSeconds(),
    };
    if (/(y+)/.test(fmt)) {
        fmt = fmt.replace(RegExp.$1, (dateObj.getFullYear() + '')
            .substr(4 - RegExp.$1.length));
    }
    for (const k in o) {
        if (new RegExp('(' + k + ')').test(fmt)) {
            fmt = fmt.replace(RegExp.$1, (RegExp.$1.length === 1)
                ? '' + o[k]
                : ('00' + o[k]).substr(('' + o[k]).length));
        }
    }
    return fmt;
};

export const dealBase64 = (str: string) => {
    if (!inNewApp) {
        return str;
    }
    try {
        return isIOS
            ? decodeURIComponent(escape(window.atob(str)))
            : str;
    }
    catch (e) {
        return str;
    }
};

export const dealArrBase64 = (arr: any[]) => {
    if (env.isIOS && arr?.length > 0) {
        const arrData: any = [];
        arr.forEach((element: string) => {
            const str = dealBase64(element);
            arrData.push(str);
        });
        return arrData;
    }
    return arr;
};

export const removeAbnormalCode = (text: string) => {
    text = text?.trim();
    if (!text) {
        return '';
    }

    const trick = text
        .replace(REGEXP_EMOJI, '')
        .trim();

    let target = '';
    for (let i = 0; i < trick.length; i++) {
        // charCodeAt 和 codePointAt 值的不同，就说明有特殊字符存在
        if (trick.charCodeAt(i) === trick.codePointAt(i)) {
            target += trick[i];
        }
    }

    return target || (text.length && !window.__settingInfo?.fullscreenVoiceMode ? '此处是特殊编码' : '');
};

/**
 * @description 清除文本中的引用角标^[xxx]^
 * @param text
 * @returns 清除后的文本
 */
export const removeReferenceSubscript = (text: string) => {
    return text.replace(REGEXP_REFERENCE_SUBSCRIPT, '');
};

export const getQueryString = (name: string, method = '') => {
    const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i');
    const r = window.location.search.substr(1).match(reg);
    if (r != null) {
        return method === 'decode' ? decodeURIComponent(r[2]) : unescape(r[2]);
    }
    return null;
};

const getMiniProgramUAString = () => {
    // 手动拼微信ua，版本号web-view拿不到，暂时写死，之后优化
    return `${window?.screen?.width || '0'}_${window?.screen?.height || '0'}_${isIOS ? 'ios' : 'android'}_1.0.0`;
};

export const addCommonParams = (url: string): string => {
    const urlObject = new URL(url);
    const searchParams = urlObject.searchParams;
    // 将新的查询参数拆分为键值对并添加到 searchParams 中
    window.__commonParams?.split('&').forEach(param => {
        const [
            key,
            value,
        ] = param.split('=');
        searchParams.append(key, value);
    });
    // 小程序单独增加参数
    if (isInMiniProgram) {
        searchParams.append('appname', 'newapp');
        searchParams.append('ua', getMiniProgramUAString());
    }
    return urlObject.toString();
};

export const buildParam = (data: {[propName: string]: string | boolean | number}) => {
    if (typeof data === 'object') {
        return Object.keys(data)
            .map(key => {
                const value = data[key] === undefined ? '' : encodeURIComponent(data[key]);
                return `${encodeURIComponent(key)}=${value}`;
            })
            .join('&');
    }
    return '';
};

export const dateDiff = (date1: Date, date2: Date, format = 'ms') => {
    if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
        throw new Error('params must be DateType');
    }
    const step = format === 'd'
        ? 1000 * 60 * 60 * 24
        : format === 'h'
            ? 1000 * 60 * 60
            : format === 'm'
                ? 1000 * 60
                : format === 's'
                    ? 1000
                    : 1;
    return (+date1 - +date2) / step;
};

export const isEmpty = (data: unknown) => {
    return !data
        || data instanceof Array && data.length === 0
        || data instanceof Object && Object.keys(data).length === 0
        || false;
};

export const getKeyCommonParams = (key: string) => {
    if (!window.__commonParams) {
        return '';
    }
    const reg = new RegExp('(^|&)' + key + '=([^&]*)(&|$)', 'i');
    const params = reg.exec(window.__commonParams);
    if (params != null) {
        return params[2] || '';
    }
    return '';
};

export const keyZipObject = (keyList: string[]) => {
    const result = {} as {
        [key: string]: string;
    };
    keyList.forEach(v => (result[v] = v));
    return result;
};

export const appVersionCompare = (version: string) => {
    if (isHarmonyOS) {
        return 1;
    }
    return versionCompare(boxVersion, version);
};

export const checkVersion = (version: string) => {
    return {
        isGreater: versionCompare(boxVersion, version) > 0,
        isLess: versionCompare(boxVersion, version) < 0,
        isEqual: versionCompare(boxVersion, version) === 0,
    };
};

export const isMockId = (id: string) => {
    return id?.includes('ASK') || id?.includes('ANSWER') || id?.includes('Mock');
};

/**
 * 是否支持新版消息通信
 * TODO每次都调用这样不太好，得让大龙哥把版本放到ua里，不要走取公参的形式
 *
 * @return  {Boolean}  true|false
 */
export const isSupportedNewMessageVersion = () => {
    // ios暂未实现
    if (!inNewApp || isPcClient()) {
        return false;
    }
    else if (isHarmonyOS) {
        return true;
    }
    return appVersionCompare('1.6') >= 0 && isAndroid;
};

// v1.6样式改版兼容判断
export const isNewVesion = () => {
    return appVersionCompare('1.6') >= 0;
};

export const roundFun = (value: number, n: number) => {
    return Math.round(value * Math.pow(10, n)) / Math.pow(10, n);
};

export const formatNumber = (num: number) => {
    num = num || 0;
    const config = {
        num,
        unit: '',
    };
    // 1w 以下
    if (num < 10000) {
        return num;
    }
    // 1w以上 1亿以下
    if (num >= 10000 && num < 10000 * 10000) {
        const number = roundFun((Math.floor((num / 10000) * 10) / 10), 1);
        config.unit = '万';
        config.num = number;
        return `${config.num}${config.unit}`;
    }
    // 1亿以上
    const number = roundFun((Math.floor((num / (10000 * 10000)) * 10) / 10), 1);
    config.unit = '亿';
    config.num = number;
    return `${config.num}${config.unit}`;
};
/**
 * @file formatNum
 * @author lijie44 <lijie44@baidu.com>
 * @description 数据处理（小数位向下取）>99999999以亿为单位展示，精确到0.1亿；>9999以万为单位展示，精确到0.1万；未达万，则直接显示
 */

export const formatNum = (num: number) => {
    num = num || 0;
    const config = {
        num,
        unit: '',
    };
    // 1w 以下
    if (num < 10000) {
        return num;
    }
    // 1w以上 1亿以下
    if (num >= 10000 && num < 10000 * 10000) {
        const number = Math.floor((num / 10000) * 10) / 10;
        config.unit = '万';
        config.num = number;
        return `${config.num}${config.unit}+`;
    }
    // 1亿以上
    const number = Math.floor((num / (10000 * 10000)) * 10) / 10;
    config.unit = '亿';
    config.num = number;
    return `${config.num}${config.unit}+`;
};

export const loadJs = (src: string) => {
    return new Promise((resolve, reject) => {
        const ref = document.getElementsByTagName('script')[0];
        let script = document.createElement('script');
        script.src = src;
        script.async = true;
        ref?.parentNode?.insertBefore(script, ref);
        script.onload = function () {
            // @ts-ignore
            script = null;
            resolve(script);
        };
        script.onerror = function (e) {
            // @ts-ignore
            script = null;
            reject(e);
        };
    });
};

export const getWebStorageByKeys = (keys: string[]) => {
    const promises: any[] = [];
    keys.map(item => {
        promises.push(useWebStorage('get', item));
    });
    return Promise.all(promises);
};

export const trimRight = (str: string, c = '\s') => {
    const reg = new RegExp(`${c}+$`);
    return str.replace(reg, '');
};

export const trimLeft = (str: string, c = '\s') => {
    const reg = new RegExp(`^${c}+`);
    return str.replace(reg, '');
};

/**
 * 获取带有frameExt参数的scheme 链接
 *
 * @param  params object
 *  {
 *   scheme: 链接
 *   souce: 来源
 *  }
 */

interface GetFrameScheme {
    scheme: string;
    source?: string;
}
export const getFrameScheme = (data: GetFrameScheme) => {
    const {scheme, source} = data;
    if (!scheme) {
        return '';
    }
    const schemeArr = scheme.split('=');
    const params = JSON.parse(decodeURIComponent(schemeArr[1]));
    const frameExt = {
        source: source || params?.source,
    };
    params.frameExt = frameExt;
    schemeArr[1] = encodeURIComponent(JSON.stringify(params));
    return schemeArr.join('=');
};

// 判断是否在指定的日期内
interface isDateInRangeParams {
    startDate: string;
    endDate: string;
}
export const isDateInRange = (params: isDateInRangeParams) => {
    const {startDate, endDate} = params;
    const dateTime = new Date().getTime();
    const startDateTime = new Date(startDate).getTime();
    const endDateTime = new Date(endDate).getTime();
    return dateTime >= startDateTime && dateTime <= endDateTime;
};

// 是否在春节活动中
export const isActivitiesInRange = () => {
    const isInRange = isDateInRange({
        startDate: ACTIVITY_DATE.START_DATE,
        endDate: ACTIVITY_DATE.END_DATE,
    });
    return appVersionCompare('2.0') >= 0 && isInRange;
};

// 数字转字符串
export const toStringValue = (obj: any): any => {
    const type = Object.prototype.toString.call(obj);
    switch (type) {
        case '[object Array]':
            const arr = [];
            for (let i = 0; i < obj.length; i++) {
                arr[i] = toStringValue(obj[i]);
            }
            return arr;
        case '[object Object]':
            for (const p in obj) {
                obj[p] = toStringValue(obj[p]);
            }
            break;
        case '[object Number]':
            obj = obj + '';
            break;
        case '[object String]':
            break;
        default:
            return;
    }
    return obj;
};

interface DevToolsFunc {
    (): void;
    opened?: boolean;
    toString(): string;
}

// 禁止debug
export const forbidDebug = () => {
    document.oncontextmenu = () => {
        return false;
    };
    const devtools: DevToolsFunc = function () { };
    devtools.toString = function () {
        this.opened = true;
        return '';
    };
    console.debug(devtools);
    setInterval(() => {
        devtools.opened = false;
        console.debug(devtools);
        if (devtools.opened) {
            // eslint-disable-next-line xss/no-location-href-assign
            window.location.href = 'about:blank';
        }
    }, 1000);

};

/**
 * @description 一格多图，去除图文消息中的br,防止换行,并给一格多图中的图片添加对应样式
 * @param {string} content 消息内容
 * @param {object} $style 一格多图样式
 * @param {string} showAvatar 会话是否展示头像
 * @return 处理后的图文消息
 */

/**
 * 按长度处理字符串
 * 例如传入【这是个很长很长很长很长很长很长的名字】
 * 返回【这是个很长很长...名字】
 *
 * @param   {string}  str  [原字符串]
 * @param   {number}  len  [最大长度]
 * @return  {[type]}
 */
export const dealStrByLength = (str: string, len = 12) => {
    const words = str ? [...str] : []; // 兼容 emoji
    return words?.length >= len
        ? `${words.slice(0, len - 5).join('')}...${words.slice(-2).join('')}`
        : str;
};

/**
 * 从传入的url链接取出图片类型
 */
export const getImageTypeFromUrl = (url?: string) => {
    if (!url) {
        return '';
    }
    // 使用正则表达式匹配图片类型
    const regex = /(jpg|gif)/i;
    const match = regex.exec(url);

    // 根据匹配结果返回图片类型
    if (match) {
        return match[0].toLowerCase();
    }
    return 'other';
};

/*
 * 计算文件大小，追加单位
 * @param   {number}  size  [size description]
 * @return  {string}        [return description]
 */
export const convertByteToUnit = (size: string): string => {
    let num = parseFloat(size);
    if (isNaN(num)) {
        return 'Invalid size';
    }
    const units: string[] = [
        'B',
        'KB',
        'MB',
        'GB',
        'TB',
    ];
    let i = 0;
    while (num >= 1000 && i < units.length - 1) { // match NA
        num /= 1000;
        i++;
    }

    return `${num.toFixed(2)}${units[i]}`;
};

/**
 * 处理长字符串的中间省略
 * @param str
 * @param maxLength
 * @returns
 */
export const ellipsizeString = (str: string, maxLength = 14): string => {
    if (str.length <= maxLength) {
        return str;
    }

    const ellipsis = '...';
    const charsToShow = maxLength - ellipsis.length;
    const frontChars = Math.ceil(charsToShow / 2);
    const backChars = Math.floor(charsToShow / 2);

    return `${str.slice(0, frontChars)}${ellipsis}${str.slice(str.length - backChars)}`;
};

/**
 * 处理文件字数格式
 * @param num
 * @returns
 */
export const formatFileWordNumber = (num: number): string => {
    if (num < 0.1 * 1000) {
        return '约0.1千字';
    }
    if (num < 9.5 * 1000) {
        return `约${(Math.round(num / 100) / 10).toFixed(1)}千字`;
    }
    return `约${(Math.round(num / 1000) / 10).toFixed(1)}万字`;
};

// 将string编码成base64（主要是针对汉字）
export const encodeBase64 = (str: string) => {

    try {
        return window.btoa(encodeURIComponent(str));
    }
    catch (e) {
        return str;
    }
};
// 将base64解码（主要是针对汉字）
export const decodeBase64 = (str: string) => {
    try {
        return decodeURIComponent(window.atob(str));
    }
    catch (e) {
        return str;
    }
};
// 处理url改成当前域名
export const formatUrl = (originUrl: string) => {
    let newUrl = '';
    try {
        const {protocol, host} = window.location;
        const urlObj = new URL(originUrl);
        newUrl = `${protocol}//${host}${urlObj.pathname + urlObj.search}`;
    }
    catch (e) {
        newUrl = '';
    }
    return newUrl;
};

// 字符串中匹配该正则的最大尾部位置
export const findLastMatchIndex = (str: string, reg: RegExp): number => {
    let regex = reg;
    try {
        regex = new RegExp(reg.source, 'g');
    }
    catch (e) {}

    let lastIndex = -1;
    const matches = str.matchAll(regex);
    for (const m of matches) {
        if (m?.index && m[0]) {
            lastIndex = m.index + m[0].length;
        }
    }

    return lastIndex;
};

// 获取cookie中的某个key
export const getCookieValue = (name: string): string | null => {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
        const [
            key,
            value,
        ] = cookie.split('=');
        if (key === name) {
            return decodeURIComponent(value);
        }
    }
    return null;
};

// 处理时间间隔显示字符串
export const dealTimeTag = (currentTime: number, createTime: number) => {
    currentTime = currentTime || Math.round(Date.now() / 1000);
    if (!createTime) {
        return '';
    }

    const serverDate = new Date(currentTime * 1000);
    const createDate = new Date(createTime * 1000);
    if (serverDate.getFullYear() !== createDate.getFullYear()) {
        return dateFormat(createDate, 'yyyy年M月d日 hh:mm');
    }
    else if (serverDate.getMonth() !== createDate.getMonth()
        || serverDate.getDate() - createDate.getDate() > 1
    ) {
        return dateFormat(createDate, 'MM月dd日 hh:mm');
    }
    else if (serverDate.getDate() - createDate.getDate() === 1) {
        return `昨天 ${dateFormat(createDate, 'hh:mm')}`;
    }

    return dateFormat(createDate, 'hh:mm');
};

// 判断公参中的sid是否包含传入的sid
export const checkSid = (arr: string[]) => {
    const sids = getKeyCommonParams('sid')?.split('-');
    return sids.find(item => arr.includes(item)) || '';
};

export const formatObj = (params: string | object) => {
    return typeof params === 'string'
        ? JSON.parse(decodeURIComponent(params))
        : params;
};

/**
 *
 * @param info 获取数字人的展示背景图
 * @returns
 */
export const getBackgroundUrlForShare = (info: any = {}) => {
    let type = '';
    let backgroundUrl = '';
    let faceSwapperUrl = '';
    let videoBackgroundImage = '';
    const {cmd = '', cfgs = {}} = info?.voiceChatMode || {};
    if (cmd === 'vis/ugc') {
        type = 'ugc';
        faceSwapperUrl = cfgs?.vis?.ugc?.faceSwapperUrl || '';
        backgroundUrl = cfgs?.vis?.ugc?.backgroundUrl || '';
    }
    else if (cmd === 'vis/kol') {
        type = 'kol';
        videoBackgroundImage = cfgs?.vis?.kol?.videoBackgroundImage || '';
    }
    else if (cmd === 'visOnline') {
        type = 'visOnline';
        videoBackgroundImage = cfgs?.visOnline?.videoBackgroundImage || '';
    }
    else if (cmd === 'dynamicVideo') {
        type = 'dynamicVideo';
        videoBackgroundImage = cfgs?.dynamicVideo?.videoBackgroundImage || '';
    }
    return {
        type,
        videoBackgroundImage,
        faceSwapperUrl,
        backgroundUrl,
    };
};

interface WhiteScreenCheckParams {
    elementId: string;
    minHeight?: number;
    cb?: () => void;
}
/**
 * 检查白屏
 *
 * @param params 检查白屏参数
 * @param params.elementId 元素ID
 * @param params.minHeight 最小高度，默认为30
 * @param params.cb 回调函数
 */
export const whiteScreenCheck = (params: WhiteScreenCheckParams) => {
    const {elementId, minHeight = 30, cb} = params;
    const dom = document.getElementById(elementId);
    const clientHeight = dom?.clientHeight || 0;
    sendYaLog(`${elementId}_whiteScreen_check_domHeight=${clientHeight}`);
    if (!dom || clientHeight < minHeight) {
        sendYaLog(`${elementId}_whiteScreen`);
        cb && cb();
    }
};
/*
* @param jsonpUrl - JSONP请求的URL，必须为字符串类型，默认值为空字符串。
* @param callbackParam - JSONP回调函数的参数名，默认值为'callback'。
* @returns {Promise<boolean>} - 返回一个Promise对象，resolve状态下为布尔值，表示是否可以使用JSONP进行fetch请求。
*/
/* eslint-disable @typescript-eslint/no-unused-vars */
async function canJsonpFetch(jsonpUrl: string, callbackParam = 'callback') {
    // 生成一个唯一的回调函数名
    // 构造JSONP URL

    // 创建一个Promise来处理JSONP响应
    return new Promise((resolve, reject) => {
        // 创建script元素
        const script = document.createElement('script');
        script.src = jsonpUrl.toString();
        // 定义全局回调函数
        (window as any).callback = (data: any) => {
            resolve(true);
            // 清理：删除script标签和全局回调
            document.body.removeChild(script);
            delete (window as any).callback;
        };

        // 处理错误情况
        script.onerror = e => {
            resolve(false);
            document.body.removeChild(script);
            delete (window as any).callback;
        };

        // 将script标签添加到文档中以开始请求
        document.body.appendChild(script);
    });
}

/**
* @description
* 通过jsonp方式检查给定的URL是否返回了404状态码。
*
* @param {string} url - 需要检查的URL，应该包含一个名为'id'的参数，用于指定资源ID。
*
* @returns {Promise<boolean>} - 返回一个Promise对象，resolve时会返回一个布尔值，表示是否返回了404状态码。
*
* @throws {Error} - 当发生错误时，将抛出一个Error对象。
*/
export async function check404ByJsonp(url: string) {
    const id = new URL(url).searchParams.get('id');
    const NOW = Date.now();
    const dataUrl = `https://boot-source.xuexi.cn/data/app/${id}.js?reedit_timestamp=${NOW}`;
    return new Promise(async (resolve, reject) => {
        try {
            const canFetch = await canJsonpFetch(dataUrl);
            console.info('hasError: ', canFetch);
            resolve(!canFetch);
        }
        catch (error) {
            reject(error);
        }
    });
}
// 从公参参中获取指定key的值
export const getCommonParamsByKey = (key: string): string | null => {
    if (!window.__commonParams) {
        return null;
    }
    const params = new URLSearchParams(window.__commonParams);
    return params.get(key);
};

// 对比两个字符串组成的数组是否相等
export const arraysIsEqual = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) {
        return false;
    }

    for (const item of arr1) {
        if (!arr2.includes(item)) {
            return false;
        }
    }

    return true;
};

// 是否为新用户, 指定时间内默认为新用户，用于sse新用户实验
export const isNewUser = () => {
    if (!inNewApp) {
        return 0;
    }
    const nowTimestamp = Date.now();
    const expireTimestamp = localStorage.getItem(NEW_USER_TIMES_KEY) || '0';
    return nowTimestamp < parseInt(expireTimestamp, 10) ? 1 : 0;
};

export const dealTypeId = (characterId = '', promptId = '', botId = '') => {
    return characterId ? `character_${characterId}`
        : promptId ? `prompt_${promptId}` : `bot_${botId}`;
};
/**
 * 判断是否出图表转换按钮
 * 条件一：当前answer不是表格&思维导图&流程图
 * 条件二：列表条数超过3条
 * 条件三：文本长度大于100
 * */
const getListCount = (domText = '') => {
    const list = domText.match(/<li>((?!<li>).|[\n\r])*?<\/li>/g);
    const listCount = list?.length || 0;
    return listCount;

};
export const canToChart = (domText: string) => {

    const MIN_LIST_COUNT = 3;
    const LANG = [
        'markdown',
        'mermaid',
        'table',
    ];
    const div = document.createElement('div');
    div.id = 'container';
    div.innerHTML = domText;

    const chart  = div.querySelector('.custom-diagram-wrapper');
    const lang = chart?.getAttribute('lang') || '';
    // 已经包含图表不出
    if (LANG.includes(lang)) {
        return false;
    }
    const htmlContent = div?.textContent?.trim() || '';
    // 去除空格和换行符
    const text = htmlContent.replace(/\s+/g, '');
    if (text.length <= 100) {
        return false;
    }

    const regArr = [
        /\n[一二三四五]、/g,
        /(\n[1-9])/g,
        /\n[a-z][\.\)]/g,
    ];
    const re =  regArr.some(reg => {
        const matches = htmlContent.match(reg);
        return matches && matches.length >= MIN_LIST_COUNT;
    });

    if (re) {
        return true;
    }

    const listCount = getListCount(domText);
    // 条件二：列表条数超过3条
    return listCount >= MIN_LIST_COUNT;
};

export function mergeAndDeduplicateByKey<T>(a: T[], b: T[], key: keyof T): T[] {
    const combinedArray = [
        ...a,
        ...b,
    ];
    // 根据 key 去重
    const uniqueArray = Array.from(
        new Map(combinedArray.map(item => [
            item[key],
            item,
        ])).values()
    );

    return uniqueArray;
}

/**
 * 获取一个完整url的host部分
 * @param url
 * @returns host部分
 */
export const getUrlHost = (url: string) => {
    try {
        const urlObj = new URL(url);
        return urlObj.host;
    }
    catch (_) {
        return '';
    }
};

/**
 * 检查链接是否为合法的网络连接 (目前只有http/https协议的链接认为有效)
 * @param link 需要校验的链接
 * @returns 是否合法
 */
export const checkValidUrl = (link?: string): boolean => {
    if (!link) {
        return false;
    }
    // XSS攻击防范
    if (link.toLowerCase().startsWith('javascript')) {
        return false;
    }

    // 解析不出host认为是无效的链接，这样可以防范其他URIScheme的链接
    // 例如：ftp://, file:/// , geo:37.786971,-122.399677 等
    if (!getUrlHost(link)) {
        return false;
    }
    // 如果是mailto协议，认为是无效的链接
    // mailto: 协议是一种特殊的协议，用于在网页中创建电子邮件链接https://zhuanlan.zhihu.com/p/415583405
    if (link.toLowerCase().startsWith('mailto')) {
        return false;
    }

    return true;
};

// 格式化时间，将秒数转换为 mm:ss 或 hh:mm:ss 格式
export const formatTimeNumber = (seconds: number) => {

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(secs).padStart(2, '0');

    if (hours > 0) {
        const formattedHours = String(hours).padStart(2, '0');
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }
    return `${formattedMinutes}:${formattedSeconds}`;

};

// 获取屏幕大小
export const getDeviceSize = () => {
    const width = window.innerWidth;
    if (width < 600) {
        return DEVICE_SIZE.SMALL; // 小屏设备
    }
    else if (width >= 600 && width < 840) {
        return DEVICE_SIZE.MEDIUM; // 中屏设备
    }
    return DEVICE_SIZE.LARGE; // 大屏设备
};
